/**
 * The district's mains, and the restriction ledger.
 *
 * The second of the five flows `DESIGN-GOVERNORS.md` §5 specifies, and the one
 * the design singles out for a property no other flow has:
 *
 * > | **Water** | Waterworks on aquifer cells; **drought-sensitive** | Families
 * > (baseline), data center cooling, some industry | Waterworks structures; in
 * > drought, browned lawns (render only) |
 *
 * Structurally it is `PowerManager`'s sibling — a supply from above, a demand
 * summed over the map, a shortfall taken down a written order, a ledger of what
 * was refused — and `docs/flows.md` sets out that shape as the one every flow
 * after the first is expected to hold. Three things about water are its own, and
 * they are the three worth reading this file for.
 *
 * ## 1. The weather is an input, and it reaches the supply
 *
 * A district's own source is rain-fed, so `getSupplyM3()` is `MAINS_M3 ×
 * rainfall`. Rainfall is a *national input* in §4's sense — physics publishes it
 * at the head of the settlement pipeline, for everybody at once — so it is state
 * that travels in the snapshot, exactly like an allocation.
 *
 * An allocation is **not** scaled by it. A polity handing a district cubic
 * metres has already run the drought down its own waterworks and its own
 * priority list; scaling the result again would charge the district for the same
 * bad weather twice. So rainfall moves what a district can draw *for itself*,
 * and an allocation replaces that number outright.
 *
 * ## 2. A garden is not a tap, and the order between them is the mechanic
 *
 *     gardens → premises → homes → federal facilities
 *
 * The first thing a shortage does is stop the watering, which is what a real
 * utility does and what the design's own trace — *browned lawns* — describes.
 * It is also the largest single load: suburban irrigation is a bigger number
 * than suburban plumbing, which is why turning it off buys so much.
 *
 * A building can therefore be short in two ways at once, and the two are counted
 * separately: `isParched` is a lawn and `isDry` is a tap. Only the second is in
 * anyone's ledger per building, because §10's migration is a sum over hardship
 * and a brown lawn is not one.
 *
 * §5 assigns water to *"Families (baseline), data center cooling, some industry"*,
 * and the cooling is last — after the taps. That is §9's drought stated as an
 * array: *"cooling water and priority power for the DCs, or baseline water and lit
 * evenings for three districts"*, resolved by default in the machine's favour,
 * because a district has no authority over a facility it never agreed to host.
 * `PowerManager`'s header argues the same order at more length.
 *
 * ## 3. It does nothing
 *
 * No cell changes, no build stalls, no family is charged, no yield falls. That
 * is not an omission: the design gives water exactly two consequences, and this
 * build has neither of the mechanics that own them. Facility production is
 * `min(power, water, staffing) × capacity` (§4), and while there are facilities
 * now, there is no labour and no compute market — so what the cooling water buys
 * is a recorded fraction and not a product (`FacilityManager`). Quality of life is
 * a sum over measured quantities (§10) and it is a later Phase 2 mechanic that
 * will read `getDryCycles`. Inventing a third consequence here — a corp that earns
 * less, a family that leaves sooner — would be an authored delta with a plausible
 * cover story, which is what Law 1 is for.
 *
 * What it does instead is be *visible*. The lawns go brown, and nothing else in
 * the world reacts, including the meadow the district never watered.
 *
 * See `docs/flows.md`.
 */

import { Canonical } from '../hashState';

/**
 * Draws and supply, in cubic metres per budget cycle. World parameters, not
 * state.
 *
 * The budget cycle is the district's day (ADR-013 D69), so these read as
 * m³/day, which is the unit a water utility bills in.
 */
export interface WaterConfig {
  /**
   * What the district's own source yields in a normal year.
   *
   * The stand-in for the aquifer a waterworks would sit on, and the only reason
   * this mechanic does anything in a world with no polity. Like the
   * interconnect it is a number a *growing* district reaches — but it is set
   * further out than the interconnect deliberately, so that in ordinary weather
   * the grid binds first and the mains binds only when it stops raining. Water
   * is the drought flow; it should behave like one.
   */
  MAINS_M3: number;
  /** A household's taps: drinking, washing, the dishwasher. Restricted last. */
  HOUSEHOLD_M3: number;
  /** One building's landscaping. Restricted first, and it is the big number. */
  GARDEN_M3: number;
  /** A corporation's process water. Restricted second. */
  COMMERCIAL_M3: number;
}

/**
 * Every draw here is a multiple of a quarter, and that is not a coincidence.
 *
 * The meter divides a deficit by a unit and rounds up. With `1.2` — a number no
 * binary float holds exactly — a district that is short by exactly two
 * households computes a deficit of `2.4000000000000004`, divides, and cuts a
 * third house. It is deterministic, so no golden master would ever catch it; it
 * is also wrong, and the fix is to spend the rates in quarters rather than to
 * subtract an epsilon somewhere and hope.
 *
 * Rainfall reintroduces the dust — `200 × 0.4` is not 80 — and that is accepted:
 * a drought landing on a boundary may take one more lawn than the arithmetic
 * demands, which is a whole consumer either way and is what a utility does.
 */
export const DEFAULT_WATER_CONFIG: WaterConfig = {
  MAINS_M3: 200,
  HOUSEHOLD_M3: 1.25,
  GARDEN_M3: 0.75,
  COMMERCIAL_M3: 3,
};

/**
 * The three things that drink, in the order they are cut.
 *
 * Exported for the reason `SHED_ORDER` and `SETTLEMENT_STAGE_ORDER` are: the
 * order *is* the policy, and a reader looking for it should find one list.
 *
 * A town stops watering its verges before it throttles its businesses, and
 * throttles its businesses before it touches the water people drink. Homes are
 * last here as they are on the grid, and for the same reason: it is what makes a
 * dry home worth measuring when one happens.
 */
export const RESTRICTION_ORDER = Object.freeze([
  'gardens',
  'premises',
  'homes',
  'facilities',
] as const);

export type WaterClass = (typeof RESTRICTION_ORDER)[number];

/**
 * What the district has on its map, as the mains sees it.
 *
 * A bag of ids assembled by the caller, not a `SurfaceSim` — the settler's
 * standard, applied to flows (ADR-013 D76, `tests/purity.test.ts`).
 *
 * The three lists overlap on purpose. A built corporation is in `gardens` and in
 * `premises`; an occupied home is in `gardens` and in `homes`. A building draws
 * in as many classes as it has reasons to.
 */
export interface WaterReading {
  /** Every built building. The district's landscaping, and the first thing cut. */
  gardens: number[];
  /** Built corporations. Process water. */
  premises: number[];
  /**
   * Occupied family homes.
   *
   * Not every home: an empty house has a lawn and nobody in it. This is the one
   * place water and power read the map differently, and the difference is the
   * whole distinction between a connection and a consumption — a meter is a
   * meter, but a tap that nobody turns draws nothing.
   */
  homes: number[];
  /**
   * What the polity's facilities on this map draw for cooling, in m³ per cycle.
   *
   * A total rather than a list, for `PowerReading.facilityKw`'s reason: this flow
   * decides how much reached the estate and `FacilityManager` decides what that
   * means. Zero in a district with nothing federal on it.
   */
  facilityM3: number;
}

/** Demand, broken out by class. Cubic metres per cycle. */
export interface WaterDemand {
  gardens: number;
  premises: number;
  homes: number;
  facilities: number;
  total: number;
}

/** The mains' complete state. See `WaterManager.save()`. */
export interface WaterSnapshot {
  allocationM3: number | null;
  rainfall: number;
  rotation: number;
  demand: WaterDemand;
  supplyM3: number;
  cutM3: number;
  parched: number[];
  dry: number[];
  facilityCutM3: number;
  dryCycles: Array<[number, number]>;
  meteredCycles: number;
  shortfallCycles: number;
  deliveredM3Cycles: number;
  cutM3Cycles: number;
  parchedGardenCycles: number;
  dryBuildingCycles: number;
  facilityCutM3Cycles: number;
}

const NO_DEMAND: WaterDemand = {
  gardens: 0, premises: 0, homes: 0, facilities: 0, total: 0,
};

/**
 * The district's water supply.
 *
 * Owned by `SurfaceSim`, metered on the same settled budget cycle as the grid,
 * snapshotted inside it. Holds no `Rng`, for `PowerManager`'s reason: a
 * shortfall is arithmetic and a rota is a counter.
 */
export class WaterManager {
  private config: WaterConfig;

  /**
   * What the polity above has allocated this district, in m³ per cycle.
   *
   * `null` means nobody has, and the district falls back to its own rain-fed
   * source. A national input (§4), and therefore state.
   */
  private allocationM3: number | null = null;

  /**
   * This season's rainfall, as a fraction of a normal year. 1 until told
   * otherwise.
   *
   * The other national input, and the only one in the game that comes from
   * physics rather than from a decision. §9's drought is this number at 0.4.
   */
  private rainfall = 1;

  /** Where the walk down each class's id list starts. Advanced once per cycle. */
  private rotation = 0;

  // --- The last metered cycle -----------------------------------------------

  private demand: WaterDemand = { ...NO_DEMAND };
  private supplyM3 = 0;
  private cutM3 = 0;
  /** Buildings whose garden was cut. A picture, not a hardship. */
  private readonly parched = new Set<number>();
  /** Buildings whose own supply was cut. The hardship. */
  private readonly dry = new Set<number>();
  /** Cubic metres the estate asked for and did not get. See `cutClass`. */
  private facilityCutM3 = 0;

  // --- The ledger -----------------------------------------------------------

  /** Cycles each building has spent dry, by id. Pruned when the building goes. */
  private readonly dryCycles = new Map<number, number>();

  private meteredCycles = 0;
  private shortfallCycles = 0;
  private deliveredM3Cycles = 0;
  private cutM3Cycles = 0;
  private parchedGardenCycles = 0;
  private dryBuildingCycles = 0;
  private facilityCutM3Cycles = 0;

  constructor(config: Partial<WaterConfig> = {}) {
    this.config = { ...DEFAULT_WATER_CONFIG, ...config };
  }

  getConfig(): WaterConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<WaterConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  // --- The national inputs --------------------------------------------------

  /**
   * What the polity above allocates this district, or `null` for none.
   *
   * Nothing inside the simulation calls it, which is the honest state of a build
   * with no polity in it. In Phase 3 it is the output of a governor's
   * `set_water_priority` (§18) running down its list.
   */
  setAllocation(m3: number | null): void {
    this.allocationM3 = m3 === null ? null : Math.max(0, m3);
  }

  /** The standing allocation, or `null` if the district is on its own source. */
  getAllocation(): number | null {
    return this.allocationM3;
  }

  /**
   * This season's rainfall, as a fraction of a normal year.
   *
   * §9's *Drought of Season 41* is `setRainfall(0.4)`. Nothing inside the
   * simulation calls this either: weather is published by the world (§4's
   * settlement stage 1), and there is no world.
   *
   * Clamped below at zero and not above, because a wet year is a real thing and
   * an aquifer that refuses to overflow is a decision nobody asked for.
   */
  setRainfall(factor: number): void {
    this.rainfall = Math.max(0, factor);
  }

  getRainfall(): number {
    return this.rainfall;
  }

  /**
   * What the district may draw: the allocation if there is one, else what the
   * weather left in its own source.
   */
  getSupplyM3(): number {
    return this.allocationM3 ?? this.config.MAINS_M3 * this.rainfall;
  }

  // --- Readings -------------------------------------------------------------

  /** Demand at the last metered cycle, by class. Zero before the first one. */
  getDemand(): WaterDemand {
    return { ...this.demand };
  }

  /** Supply at the last metered cycle. Zero before the first one. */
  getMeteredSupplyM3(): number {
    return this.supplyM3;
  }

  /** Water refused at the last metered cycle. Zero when demand fitted. */
  getCutM3(): number {
    return this.cutM3;
  }

  /** True while this building's garden is unwatered: a brown lawn, and no more. */
  isParched(buildingId: number): boolean {
    return this.parched.has(buildingId);
  }

  /** True while this building's own supply is cut. The one that counts. */
  isDry(buildingId: number): boolean {
    return this.dry.has(buildingId);
  }

  /** Whether anything is restricted at all. The cheap question. */
  get restricting(): boolean {
    return this.parched.size > 0 || this.dry.size > 0;
  }

  /** Brown lawns at the last metered cycle. */
  getParchedCount(): number {
    return this.parched.size;
  }

  /** Buildings whose supply was cut at the last metered cycle. */
  getDryCount(): number {
    return this.dry.size;
  }

  /**
   * What fraction of the estate's cooling water was delivered, in [0, 1].
   *
   * `PowerManager.getFacilityFraction`'s twin, and the other half of the `min` a
   * facility's uptime is (`FacilityManager.meter`). 1 when there is no facility.
   */
  getFacilityFraction(): number {
    const demand = this.demand.facilities;
    if (demand <= 0) return 1;
    return Math.min(1, Math.max(0, (demand - this.facilityCutM3) / demand));
  }

  /** Cubic metres the estate was refused at the last metered cycle. */
  getFacilityCutM3(): number {
    return this.facilityCutM3;
  }

  /**
   * Cycles this building has spent with its supply cut, over its whole life.
   *
   * The water half of what §12 puts in a mayor's face when a family leaves, and
   * the input §10's migration will sum over beside brownout-hours. Gardens are
   * deliberately not in it: a lawn is a picture and a tap is a measurement.
   *
   * Read it before the building is removed; the entry goes when it does.
   */
  getDryCycles(buildingId: number): number {
    return this.dryCycles.get(buildingId) ?? 0;
  }

  /** The lifetime totals. Monotone sums, so a season's flows are a subtraction. */
  getLedger() {
    return {
      meteredCycles: this.meteredCycles,
      shortfallCycles: this.shortfallCycles,
      deliveredM3Cycles: this.deliveredM3Cycles,
      cutM3Cycles: this.cutM3Cycles,
      parchedGardenCycles: this.parchedGardenCycles,
      dryBuildingCycles: this.dryBuildingCycles,
      facilityCutM3Cycles: this.facilityCutM3Cycles,
    };
  }

  // --- The meter ------------------------------------------------------------

  /**
   * Meter one budget cycle: sum the demand, cut what does not fit, record it.
   *
   * Called once per settled budget cycle, from the same four lines in
   * `SurfaceSim.update()` that meter the grid. The reasoning is ADR-013 D69's
   * and is not restated: the cycle counter is cadence-invariant, so a drought is
   * the same length of world at 6 Hz as at 60.
   */
  meter(reading: WaterReading): void {
    const { HOUSEHOLD_M3, GARDEN_M3, COMMERCIAL_M3 } = this.config;

    const demand: WaterDemand = {
      gardens: reading.gardens.length * GARDEN_M3,
      premises: reading.premises.length * COMMERCIAL_M3,
      homes: reading.homes.length * HOUSEHOLD_M3,
      facilities: Math.max(0, reading.facilityM3),
      total: 0,
    };
    demand.total = demand.gardens + demand.premises + demand.homes + demand.facilities;

    const supply = this.getSupplyM3();

    this.demand = demand;
    this.supplyM3 = supply;
    this.parched.clear();
    this.dry.clear();
    this.facilityCutM3 = 0;
    this.cutM3 = 0;

    let deficit = demand.total - supply;
    if (deficit > 0) {
      for (const cls of RESTRICTION_ORDER) {
        if (deficit <= 0) break;
        deficit -= this.cutClass(cls, reading, deficit);
      }
    }

    // Advanced every metered cycle, comfortable or not, so a drought that comes
    // and goes does not always start on the same street (ADR-013 D71).
    this.rotation++;

    const delivered = demand.total - this.cutM3;
    this.meteredCycles++;
    this.deliveredM3Cycles += delivered;
    this.cutM3Cycles += this.cutM3;
    if (this.cutM3 > 0) this.shortfallCycles++;

    for (const id of sortedIds(this.dry)) {
      this.dryCycles.set(id, (this.dryCycles.get(id) ?? 0) + 1);
    }
    this.parchedGardenCycles += this.parched.size;
    this.dryBuildingCycles += this.dry.size;
    this.facilityCutM3Cycles += this.facilityCutM3;

    this.pruneDryCycles(reading);
  }

  /**
   * Cut one class until the deficit is covered. Returns the m³ taken off.
   *
   * Whole consumers, starting at `rotation` and wrapping — you cannot water four
   * fifths of a lawn either, and the last one taken overshoots by less than its
   * own draw.
   *
   * The facilities are the exception, exactly as they are on the grid: cooling is
   * throttled by the remaining deficit and a facility runs proportionally slower,
   * because §4's production rule is a multiplication and not a switch.
   */
  private cutClass(cls: WaterClass, reading: WaterReading, deficit: number): number {
    if (cls === 'facilities') {
      const demand = this.demand.facilities;
      if (demand <= 0) return 0;
      const taken = Math.min(demand, deficit);
      this.facilityCutM3 = taken;
      this.cutM3 += taken;
      return taken;
    }

    const unit = cls === 'gardens' ? this.config.GARDEN_M3
      : cls === 'premises' ? this.config.COMMERCIAL_M3
      : this.config.HOUSEHOLD_M3;
    const ids = cls === 'gardens' ? reading.gardens
      : cls === 'premises' ? reading.premises
      : reading.homes;

    if (unit <= 0 || ids.length === 0) return 0;

    const target = cls === 'gardens' ? this.parched : this.dry;
    const count = Math.min(ids.length, Math.ceil(deficit / unit));
    const start = this.rotation % ids.length;
    for (let i = 0; i < count; i++) {
      target.add(ids[(start + i) % ids.length]);
    }
    const taken = count * unit;
    this.cutM3 += taken;
    return taken;
  }

  /**
   * Forget buildings that are no longer on the map.
   *
   * `PowerManager.pruneDarkCycles`' reasoning, unchanged: a demolished house has
   * nobody to report to, and a map keyed by a monotone id would leak over
   * sixty-four seasons.
   */
  private pruneDryCycles(reading: WaterReading): void {
    if (this.dryCycles.size === 0) return;
    const present = new Set<number>();
    for (const id of reading.gardens) present.add(id);
    for (const id of reading.premises) present.add(id);
    for (const id of reading.homes) present.add(id);

    for (const id of Array.from(this.dryCycles.keys()).sort((a, b) => a - b)) {
      if (!present.has(id)) this.dryCycles.delete(id);
    }
  }

  // --- State ---------------------------------------------------------------

  save(): WaterSnapshot {
    return {
      allocationM3: this.allocationM3,
      rainfall: this.rainfall,
      rotation: this.rotation,
      demand: { ...this.demand },
      supplyM3: this.supplyM3,
      cutM3: this.cutM3,
      parched: sortedIds(this.parched),
      dry: sortedIds(this.dry),
      facilityCutM3: this.facilityCutM3,
      dryCycles: Array.from(this.dryCycles.keys())
        .sort((a, b) => a - b)
        .map((id) => [id, this.dryCycles.get(id)!] as [number, number]),
      meteredCycles: this.meteredCycles,
      shortfallCycles: this.shortfallCycles,
      deliveredM3Cycles: this.deliveredM3Cycles,
      cutM3Cycles: this.cutM3Cycles,
      parchedGardenCycles: this.parchedGardenCycles,
      dryBuildingCycles: this.dryBuildingCycles,
      facilityCutM3Cycles: this.facilityCutM3Cycles,
    };
  }

  load(state: WaterSnapshot): void {
    this.allocationM3 = state.allocationM3;
    this.rainfall = state.rainfall;
    this.rotation = state.rotation;
    this.demand = { ...state.demand };
    this.supplyM3 = state.supplyM3;
    this.cutM3 = state.cutM3;
    this.parched.clear();
    for (const id of state.parched) this.parched.add(id);
    this.dry.clear();
    for (const id of state.dry) this.dry.add(id);
    this.facilityCutM3 = state.facilityCutM3;
    this.dryCycles.clear();
    for (const [id, cycles] of state.dryCycles) this.dryCycles.set(id, cycles);
    this.meteredCycles = state.meteredCycles;
    this.shortfallCycles = state.shortfallCycles;
    this.deliveredM3Cycles = state.deliveredM3Cycles;
    this.cutM3Cycles = state.cutM3Cycles;
    this.parchedGardenCycles = state.parchedGardenCycles;
    this.dryBuildingCycles = state.dryBuildingCycles;
    this.facilityCutM3Cycles = state.facilityCutM3Cycles;
  }

  /**
   * Order-stable view of the mains, for hashing — and nothing before the flow
   * has read anything.
   *
   * The guard is `PowerManager`'s with one more clause. A district that has
   * never metered, has no allocation and has had normal weather is a district
   * with no water flow in it, and the key is omitted rather than written as a
   * bag of zeroes. Rainfall is in the guard because it is state: a world told it
   * is in a drought is a different world before a single cycle is metered, and
   * it must hash as one.
   */
  canonicalState(): Canonical | undefined {
    if (this.meteredCycles === 0 && this.allocationM3 === null && this.rainfall === 1) {
      return undefined;
    }

    // The facility terms are left out while they are zero. `PowerManager`'s
    // `canonicalState` says why, and why it means ADR-015 moved no hash.
    const demand: Record<string, Canonical> = {
      gardens: this.demand.gardens,
      premises: this.demand.premises,
      homes: this.demand.homes,
      total: this.demand.total,
    };
    if (this.demand.facilities > 0) demand.facilities = this.demand.facilities;

    const state: Record<string, Canonical> = {
      allocationM3: this.allocationM3,
      rainfall: this.rainfall,
      rotation: this.rotation,
      demand,
      supplyM3: this.supplyM3,
      cutM3: this.cutM3,
      parched: sortedIds(this.parched),
      dry: sortedIds(this.dry),
      dryCycles: Array.from(this.dryCycles.keys())
        .sort((a, b) => a - b)
        .map((id) => `${id}:${this.dryCycles.get(id)}`),
      meteredCycles: this.meteredCycles,
      shortfallCycles: this.shortfallCycles,
      deliveredM3Cycles: this.deliveredM3Cycles,
      cutM3Cycles: this.cutM3Cycles,
      parchedGardenCycles: this.parchedGardenCycles,
      dryBuildingCycles: this.dryBuildingCycles,
    };
    if (this.facilityCutM3 > 0) state.facilityCutM3 = this.facilityCutM3;
    if (this.facilityCutM3Cycles > 0) {
      state.facilityCutM3Cycles = this.facilityCutM3Cycles;
    }
    return state;
  }
}

/** Ids ascending. Iteration that writes a ledger cannot use `Set` order. */
function sortedIds(set: ReadonlySet<number>): number[] {
  return Array.from(set).sort((a, b) => a - b);
}
